/**
 * Properties form generated from a component's fields (SPEC.md §1.1): text, textarea, richtext, number, boolean,
 * select, image, link, color, icon, object, list (add/remove/reorder within min…max, nested), slot.
 * Values are the instance's raw props; missing values show the field default.
 */
import React, { createContext, useContext, useMemo, useRef, useState } from 'react';
import { AutoComplete, Button, Checkbox, ColorPicker, Empty, Input, InputNumber, Popover, Select, Space, Switch, Tooltip, Typography, Upload, message } from 'antd';
import {
    ArrowDownOutlined, ArrowUpOutlined, CaretDownOutlined, CaretRightOutlined, CopyOutlined, DeleteOutlined, PlusOutlined,
    QuestionCircleOutlined, UploadOutlined, CloseOutlined, PictureOutlined,
} from '@ant-design/icons';
import { useTranslation } from '../../../../shared/i18n';
import RichEditor from '../RichEditor';
import { ICON_NAMES, iconSVG } from './icons';
import { MediaPickerModal } from '../Media';

const { Text } = Typography;

/** ctx: { pages: [{path,title}], uploadImage?: (file) => Promise<url>, onAddToSlot?: (slot) => void, slotCounts: {slot: n} } */
export const FieldCtx = createContext({});

const clone = (x) => (x === undefined ? undefined : JSON.parse(JSON.stringify(x)));

const typeDefault = (f) => {
    if (f.default !== undefined) return clone(f.default);
    switch (f.type) {
        case 'number': return f.min != null ? f.min : 0;
        case 'boolean': return false;
        case 'select': return f.options?.[0]?.value ?? '';
        case 'image': return { src: '', alt: '' };
        case 'link': return { href: '', label: '' };
        case 'list': return [];
        case 'object': return {};
        default: return '';
    }
};

const Label = ({ f, name, extra }) => {
    const { t } = useTranslation();
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4, minHeight: 22 }}>
            <Text strong style={{ fontSize: 12 }}>{f.label || name}</Text>
            {f.required && <Text type="danger">*</Text>}
            {f.help && <Tooltip title={f.help}><QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} /></Tooltip>}
            <div style={{ flex: 1 }} />
            {extra}
            {void t}
        </div>
    );
};

// ---------- individual inputs ----------

export const IconPicker = ({ value, onChange }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const names = ICON_NAMES.filter((n) => !q || n.includes(q.toLowerCase()));
    const grid = (
        <div style={{ width: 280 }}>
            <Input size="small" allowClear placeholder={t('Search icons')} value={q} onChange={(e) => setQ(e.target.value)} style={{ marginBottom: 8 }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, maxHeight: 240, overflow: 'auto' }}>
                {names.map((n) => (
                    <Tooltip key={n} title={n} mouseEnterDelay={0.3}>
                        <button type="button" onClick={() => { onChange(n); setOpen(false); }}
                            style={{ height: 34, border: n === value ? '2px solid #2357ff' : '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: 'pointer', color: '#111' }}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: iconSVG(n).replace('class="yc-icon"', 'style="width:18px;height:18px"') }} />
                    </Tooltip>
                ))}
            </div>
        </div>
    );
    return (
        <Popover content={grid} trigger="click" open={open} onOpenChange={setOpen} placement="leftTop">
            <Button style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-start' }}>
                {/* eslint-disable-next-line react/no-danger */}
                {value ? <span style={{ display: 'inline-flex', width: 18, height: 18 }} dangerouslySetInnerHTML={{ __html: iconSVG(value).replace('class="yc-icon"', 'style="width:18px;height:18px"') }} /> : null}
                <span>{value || t('Choose icon')}</span>
            </Button>
        </Popover>
    );
};

export const ImageInput = ({ value, onChange, compact }) => {
    const { t } = useTranslation();
    const ctx = useContext(FieldCtx);
    const v = value && typeof value === 'object' ? value : { src: '', alt: '' };
    const [busy, setBusy] = useState(false);
    const [picking, setPicking] = useState(false);
    const set = (patch) => onChange({ ...v, ...patch });
    const upload = async (file) => {
        if (!ctx.uploadImage) return false;
        setBusy(true);
        try {
            const res = await ctx.uploadImage(file);
            if (res?.src || typeof res === 'string') set({ src: res.src || res, width: res.width || 0, height: res.height || 0 });
        } catch (e) {
            if (!e?.toasted && !e?.status) message.error(e?.error || t('Upload failed'));
        } finally {
            setBusy(false);
        }
        return false;
    };
    return (
        <div>
            {v.src ? (
                <div style={{ position: 'relative', marginBottom: 6, background: '#f3f4f6', borderRadius: 6, overflow: 'hidden', textAlign: 'center' }}>
                    <Tooltip title={t('Click to set the focal point')} mouseEnterDelay={0.6}>
                        <span style={{ position: 'relative', display: 'inline-block', cursor: 'crosshair', lineHeight: 0 }} onClick={(e) => {
                            const r = e.currentTarget.getBoundingClientRect();
                            set({ focal: { x: Math.round(((e.clientX - r.left) / r.width) * 100), y: Math.round(((e.clientY - r.top) / r.height) * 100) } });
                        }}>
                            <img src={v.src} alt={v.alt || ''} style={{ maxHeight: compact ? 80 : 120, maxWidth: '100%', display: 'inline-block' }} />
                            {v.focal && <span style={{ position: 'absolute', left: `${v.focal.x}%`, top: `${v.focal.y}%`, width: 14, height: 14, margin: '-7px 0 0 -7px', borderRadius: '50%', border: '2px solid #fff', boxShadow: '0 0 0 1px #2357ff', background: 'rgba(35,87,255,.6)', pointerEvents: 'none' }} />}
                        </span>
                    </Tooltip>
                    <Button size="small" shape="circle" icon={<CloseOutlined />} style={{ position: 'absolute', top: 4, right: 4 }} onClick={() => { const { focal, ...rest } = v; onChange({ ...rest, src: '', width: 0, height: 0 }); }} />
                </div>
            ) : null}
            <Space.Compact style={{ width: '100%' }}>
                <Input size="small" placeholder="https://…" value={v.src} onChange={(e) => set({ src: e.target.value })} />
                {ctx.uploadImage && (
                    <Upload accept="image/*" showUploadList={false} beforeUpload={upload}>
                        <Tooltip title={t('Upload')}><Button size="small" icon={<UploadOutlined />} loading={busy} /></Tooltip>
                    </Upload>
                )}
                {ctx.siteId && (
                    <Tooltip title={t('Media library')}><Button size="small" icon={<PictureOutlined />} onClick={() => setPicking(true)} /></Tooltip>
                )}
            </Space.Compact>
            {ctx.siteId && (
                <MediaPickerModal open={picking} siteId={ctx.siteId} onClose={() => setPicking(false)}
                    onPick={(m) => set({ src: m.url, alt: v.alt || m.alt || '', width: m.width || 0, height: m.height || 0 })} />
            )}
            <Input size="small" placeholder={t('Alt text')} value={v.alt} onChange={(e) => set({ alt: e.target.value })} style={{ marginTop: 4 }} />
        </div>
    );
};

/** instance `_style`: per-device visibility, top/bottom padding, background */
export const BlockStyle = ({ value, onChange }) => {
    const { t } = useTranslation();
    const v = value || {};
    const patch = (k, x) => {
        const next = { ...v };
        if (x === null || x === undefined || x === '' || (Array.isArray(x) && !x.length)) delete next[k]; else next[k] = x;
        onChange(next);
    };
    const row = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8, fontSize: 12 };
    return (
        <div>
            <div style={{ marginBottom: 8, fontSize: 12 }}>{t('Hide on')}</div>
            <Checkbox.Group style={{ marginBottom: 8 }} value={v.hide_on || []} onChange={(x) => patch('hide_on', x)}
                options={[{ value: 'mobile', label: t('Mobile') }, { value: 'tablet', label: t('Tablet') }, { value: 'desktop', label: t('Desktop') }]} />
            <div style={row}><span>{t('Padding top')}</span><InputNumber size="small" min={0} max={400} addonAfter="px" style={{ width: 110 }} value={v.padding_top} onChange={(x) => patch('padding_top', x)} /></div>
            <div style={row}><span>{t('Padding bottom')}</span><InputNumber size="small" min={0} max={400} addonAfter="px" style={{ width: 110 }} value={v.padding_bottom} onChange={(x) => patch('padding_bottom', x)} /></div>
            <div style={row}><span>{t('Background')}</span>
                <ColorPicker size="small" allowClear value={v.background || null} onChange={(c) => patch('background', c.toHexString())} onClear={() => patch('background', null)} />
            </div>
        </div>
    );
};

export const LinkInput = ({ value, onChange }) => {
    const { t } = useTranslation();
    const ctx = useContext(FieldCtx);
    const v = value && typeof value === 'object' ? value : { href: '', label: '' };
    const set = (patch) => onChange({ ...v, ...patch });
    const options = useMemo(() => (ctx.pages || []).filter((p) => !String(p.path).includes(':')).map((p) => ({ value: p.path, label: `${p.path}  ·  ${p.title || ''}` })), [ctx.pages]);
    return (
        <div style={{ display: 'grid', gap: 4 }}>
            <Input size="small" placeholder={t('Label')} value={v.label} onChange={(e) => set({ label: e.target.value })} />
            <AutoComplete size="small" options={options} value={v.href} onChange={(href) => set({ href })} placeholder={t('URL or page path')}
                filterOption={(input, o) => String(o.value).toLowerCase().includes(String(input).toLowerCase())} />
            <Checkbox checked={v.target === '_blank'} onChange={(e) => set({ target: e.target.checked ? '_blank' : '' })}>
                <Text style={{ fontSize: 12 }}>{t('Open in a new tab')}</Text>
            </Checkbox>
        </div>
    );
};

const itemSummary = (f, item, i) => {
    const tpl = f.itemLabel;
    const pick = (obj, path) => path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
    if (tpl) {
        const s = tpl.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, k) => {
            const x = pick(item, k);
            return x == null ? '' : (typeof x === 'object' ? (x.label || x.src || x.href || '') : String(x));
        }).trim();
        if (s) return s.replace(/<[^>]+>/g, '').slice(0, 60);
    }
    for (const [k, sf] of Object.entries(f.item || {})) {
        const x = item?.[k];
        if (['text', 'textarea'].includes(sf.type) && x) return String(x).slice(0, 60);
        if (sf.type === 'link' && x?.label) return x.label;
    }
    return `#${i + 1}`;
};

const ListInput = ({ f, value, onChange, depth }) => {
    const { t } = useTranslation();
    const items = Array.isArray(value) ? value : [];
    const [open, setOpen] = useState(() => new Set(items.length === 1 ? [0] : []));
    const dragFrom = useRef(null);
    const max = f.max != null ? Math.min(f.max, 100) : 100;
    const min = f.min != null ? f.min : 0;
    const set = (next) => onChange(next);
    const toggle = (i) => setOpen((s) => { const n = new Set(s); if (n.has(i)) n.delete(i); else n.add(i); return n; });
    const moveItem = (from, to) => {
        if (to < 0 || to >= items.length || from === to) return;
        const next = [...items];
        const [x] = next.splice(from, 1);
        next.splice(to, 0, x);
        set(next);
        setOpen(new Set([to]));
    };
    const add = () => {
        const item = {};
        Object.entries(f.item || {}).forEach(([k, sf]) => { if (sf.default !== undefined) item[k] = clone(sf.default); });
        set([...items, item]);
        setOpen(new Set([items.length]));
    };
    return (
        <div>
            {items.length === 0 && <Text type="secondary" style={{ fontSize: 12 }}>{t('No items')}</Text>}
            {items.map((it, i) => (
                <div key={i}
                    onDragOver={(e) => { if (dragFrom.current !== null) e.preventDefault(); }}
                    onDrop={(e) => { e.preventDefault(); if (dragFrom.current !== null) moveItem(dragFrom.current, i); dragFrom.current = null; }}
                    style={{ border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 6, background: depth % 2 ? '#fafafa' : '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '2px 4px', cursor: 'pointer' }}
                        draggable onDragStart={(e) => { dragFrom.current = i; e.dataTransfer.effectAllowed = 'move'; e.stopPropagation(); }}
                        onClick={() => toggle(i)}>
                        {open.has(i) ? <CaretDownOutlined style={{ fontSize: 10 }} /> : <CaretRightOutlined style={{ fontSize: 10 }} />}
                        <Text style={{ flex: 1, fontSize: 12, minWidth: 0 }} ellipsis>{itemSummary(f, it, i)}</Text>
                        <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
                            <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => moveItem(i, i - 1)} />
                            <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={i === items.length - 1} onClick={() => moveItem(i, i + 1)} />
                            <Tooltip title={t('Duplicate')}><Button size="small" type="text" icon={<CopyOutlined />} disabled={items.length >= max} onClick={() => { const next = [...items]; next.splice(i + 1, 0, clone(it)); set(next); }} /></Tooltip>
                            <Tooltip title={t('Delete')}><Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={items.length <= min} onClick={() => { set(items.filter((_, j) => j !== i)); setOpen(new Set()); }} /></Tooltip>
                        </span>
                    </div>
                    {open.has(i) && (
                        <div style={{ padding: '4px 8px 8px' }}>
                            <FieldForm fields={f.item || {}} value={it || {}} depth={depth + 1}
                                onChange={(v) => { const next = [...items]; next[i] = v; set(next); }} />
                        </div>
                    )}
                </div>
            ))}
            <Button size="small" type="dashed" block icon={<PlusOutlined />} onClick={add} disabled={items.length >= max}>
                {t('Add item')}{f.max != null ? ` (${items.length}/${max})` : ''}
            </Button>
        </div>
    );
};

const SlotInfo = ({ name }) => {
    const { t } = useTranslation();
    const ctx = useContext(FieldCtx);
    const n = ctx.slotCounts?.[name] || 0;
    return (
        <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>{t('{{n}} blocks', { n })}</Text>
            {ctx.onAddToSlot && <Button size="small" icon={<PlusOutlined />} onClick={() => ctx.onAddToSlot(name)}>{t('Add block')}</Button>}
        </Space>
    );
};

export const FieldInput = ({ f, name, value, onChange, depth = 0 }) => {
    const v = value === undefined || value === null ? typeDefault(f) : value;
    switch (f.type) {
        case 'text':
            return <Input size="small" value={v} maxLength={f.maxLength || undefined} onChange={(e) => onChange(e.target.value)} />;
        case 'textarea':
            return <Input.TextArea size="small" value={v} maxLength={f.maxLength || undefined} autoSize={{ minRows: 2, maxRows: 10 }} onChange={(e) => onChange(e.target.value)} />;
        case 'richtext':
            return <RichEditor mode="richtext" value={v} onChange={onChange} minHeight={90} />;
        case 'number':
            return <InputNumber size="small" style={{ width: '100%' }} value={v} min={f.min ?? undefined} max={f.max ?? undefined} step={f.step || 1}
                onChange={(x) => onChange(x === null ? undefined : x)} />;
        case 'boolean':
            return <Switch size="small" checked={!!v} onChange={onChange} />;
        case 'select':
            return <Select size="small" style={{ width: '100%' }} value={v} onChange={onChange}
                options={(f.options || []).map((o) => ({ value: o.value, label: o.label ?? String(o.value) }))} />;
        case 'color':
            return <ColorPicker size="small" value={typeof v === 'string' && v ? v : undefined} showText disabledAlpha
                onChange={(c) => onChange(c.toHexString())} />;
        case 'icon':
            return <IconPicker value={typeof v === 'string' ? v : ''} onChange={onChange} />;
        case 'image':
            return <ImageInput value={v} onChange={onChange} />;
        case 'link':
            return <LinkInput value={v} onChange={onChange} />;
        case 'object':
            return (
                <div style={{ borderLeft: '2px solid #e5e7eb', paddingLeft: 8 }}>
                    <FieldForm fields={f.fields || {}} value={v || {}} onChange={onChange} depth={depth + 1} />
                </div>
            );
        case 'list':
            return <ListInput f={f} value={v} onChange={onChange} depth={depth} />;
        case 'slot':
            return <SlotInfo name={name} />;
        default:
            return <Text type="secondary">{f.type}</Text>;
    }
};

/** inline types get label + input on one row */
const INLINE = new Set(['boolean', 'color', 'number']);

const FieldForm = ({ fields, value, onChange, depth = 0 }) => {
    const { t } = useTranslation();
    const entries = Object.entries(fields || {});
    if (!entries.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No settings')} />;
    const v = value || {};
    return (
        <div>
            {entries.map(([key, f]) => {
                const input = <FieldInput f={f} name={key} value={v[key]} depth={depth} onChange={(x) => {
                    const next = { ...v };
                    if (x === undefined) delete next[key]; else next[key] = x;
                    onChange(next);
                }} />;
                if (INLINE.has(f.type)) {
                    return (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <div style={{ flex: 1, minWidth: 0 }}><Label f={f} name={key} /></div>
                            <div style={{ width: f.type === 'boolean' ? 'auto' : 130 }}>{input}</div>
                        </div>
                    );
                }
                return (
                    <div key={key} style={{ marginBottom: 12 }}>
                        <Label f={f} name={key} />
                        {input}
                    </div>
                );
            })}
        </div>
    );
};

export default FieldForm;
